import { useCallback, useState, useEffect, FC, useMemo } from 'react';
import {
    Modal,
    ModalContent,
    ModalHeader,
    ModalBody,
    ModalFooter,
    useDisclosure,
    Spinner,
} from "@nextui-org/react";
import { checkType, CtyButton, CtyShowToast, useHasApplicationResource, optionalCodePayload } from '@circuitry-ai/doc-ui';
import { useTranslation } from 'react-i18next';
import { useForm } from 'react-hook-form';
import CredentialForm, { CredentialFormDetails } from './credentialForm';
import { 
    useCreateCredentialMutation, 
    useUpdateCredentialMutation, 
    useGetCredentialByIdQuery,
    useLazyTestCredentialQuery,
    CredentialPayload
} from '@circuitry-ai/doc-data';

interface CredentialModalProps {
    showModal: boolean;
    onDialogClose: () => void;
    credentialId?: string;
    onSuccess?: () => void;
}

const formatStoredJsonField = (value: unknown): string => {
    if (!value) return '';
    return typeof value === 'string' ? value : JSON.stringify(value, null, 2);
};

const parseJsonObject = (value: string | undefined): Record<string, unknown> => {
    if (!value?.trim()) return {};
    try {
        const parsed = JSON.parse(value);
        return parsed && typeof parsed === 'object' && !Array.isArray(parsed)
            ? parsed as Record<string, unknown>
            : {};
    } catch {
        return {};
    }
};

const CredentialModal: FC<CredentialModalProps> = ({
    showModal,
    onDialogClose,
    credentialId,
    onSuccess
}) => {
    const { t } = useTranslation();
    const { onClose } = useDisclosure();
    const hasCredentialsAccess = useHasApplicationResource('credentials');
    const [isSubmitted, setIsSubmitted] = useState(false);
    const [errorMessage, setErrorMessage] = useState('');
    const [testResult, setTestResult] = useState<{ type: 'success' | 'error'; message: string } | null>(null);
    const [credentialCode, setCredentialCode] = useState('');

    // Fetch credential data when editing
    const { data: credentialDetails, isLoading: isLoadingCredential } = useGetCredentialByIdQuery(
        credentialId || '', 
        { skip: !credentialId || !showModal }
    );

    const [createCredential, { isLoading: isCreatingCredential }] = useCreateCredentialMutation();
    const [updateCredential, { isLoading: isUpdatingCredential }] = useUpdateCredentialMutation();
    const [triggerTestCredential, { isLoading: isTestingCredential }] = useLazyTestCredentialQuery();

    const isLoading = isCreatingCredential || isUpdatingCredential;

    // Transform credential data to form values
    const initialFormValues = useMemo(() => {
        if (credentialId && credentialDetails) {
            // Handle different API response structures
            const credential = credentialDetails;
            const config = credential?.config || {};
            const secrets = credential?.secrets || credential?.masked_secrets || {};
            const tags = credential?.tags || [];
            const customSecrets = credential?.credential_type === 'custom'
                ? Object.entries(secrets as Record<string, string>)
                    .map(([key, value]) => ({ key, value }))
                : [];
            const valueType = credential?.credential_type === 'key_value' ? (config?.value_type || 'string') : '';
            const keyValuePairs = credential?.credential_type === 'key_value' && valueType === 'key_value' && secrets?.value && typeof secrets.value === 'object'
                ? Object.entries(secrets.value as Record<string, string>).map(([key, value]) => ({ key, value }))
                : [];
            const valueObject = credential?.credential_type === 'key_value' && valueType === 'object'
                ? (typeof secrets?.value === 'string' ? secrets.value : JSON.stringify(secrets?.value ?? {}))
                : '';
            const configRecord = (config || {}) as Record<string, unknown>;
            return {
                name: credential?.name || '',
                description: credential?.description || '',
                credential_type: credential?.credential_type || '',
                // Config fields
                token_url: config?.token_url || '',
                grant_type: credential?.grant_type || config?.grant_type || '',
                scope: config?.scope || '',
                token_ttl_seconds: (() => {
                    const v = configRecord?.token_ttl_seconds;
                    if (typeof v === 'number' && !Number.isNaN(v)) return v;
                    if (typeof v === 'string' && v.trim() !== '') {
                        const n = Number(v);
                        if (!Number.isNaN(n)) return n;
                    }
                    return undefined;
                })(),
                access_cookie_name: configRecord?.access_cookie_name || '',
                header_name: config?.header_name || '',
                prefix: config?.prefix || '',
                http_method: config?.http_method || '',
                request_body_template: formatStoredJsonField(config?.request_body_template),
                response_token_path: config?.response_token_path || '',
                response_expiry_path: config?.response_expiry_path || '',
                headers: formatStoredJsonField(config?.headers),
                key: config?.key || '',
                value_type: valueType,
                // Secrets fields
                client_id: secrets?.client_id || '',
                client_secret: secrets?.client_secret || '',
                username: secrets?.username || '',
                password: secrets?.password || '',
                api_key: secrets?.api_key || '',
                api_secret: secrets?.api_secret || '',
                token: secrets?.token || '',
                value: typeof secrets?.value === 'string' ? secrets.value : '',
                value_object: valueObject,
                key_value_pairs: keyValuePairs,
                custom_secrets: customSecrets,
                tags: Array.isArray(tags) ? tags : []
            };
        }
        return undefined;
    }, [credentialId, credentialDetails]);

    const { control, getValues, setValue, watch, formState: { errors, isValid }, trigger, reset } = useForm<CredentialFormDetails>({
        mode: "onChange",
        defaultValues: {
            name: '',
            description: '',
            credential_type: '',
            // Config fields
            token_url: '',
            grant_type: 'client_credentials',
            scope: '',
            token_ttl_seconds: undefined,
            access_cookie_name: '',
            header_name: '',
            prefix: 'Bearer',
            http_method: 'POST',
            request_body_template: '',
            response_token_path: '',
            response_expiry_path: '',
            headers: '',
            key: '',
            value_type: '',
            // Secrets fields
            client_id: '',
            client_secret: '',
            username: '',
            password: '',
            api_key: '',
            api_secret: '',
            token: '',
            value: '',
            value_object: '',
            key_value_pairs: [],
            custom_secrets: [],
            tags: []
        }
    });

    const watchedCredentialType = watch('credential_type');

    // Update form values when initialFormValues changes (after data is loaded)
    useEffect(() => {
        if (initialFormValues) {
            Object.keys(initialFormValues).forEach((key) => {
                const value = initialFormValues[key as keyof typeof initialFormValues];
                if (value !== undefined) {
                    setValue(key as keyof CredentialFormDetails, value as CredentialFormDetails[keyof CredentialFormDetails]);
                }
            });
        } else if (!credentialId) {
            // Reset form for new credential
            reset({
                name: '',
                description: '',
                credential_type: '',
                token_url: '',
                grant_type: 'client_credentials',
                scope: '',
                token_ttl_seconds: undefined,
                access_cookie_name: '',
                header_name: '',
                prefix: '',
                http_method: '',
                request_body_template: '',
                response_token_path: '',
                response_expiry_path: '',
                headers: '',
                key: '',
                value_type: '',
                client_id: '',
                client_secret: '',
                username: '',
                password: '',
                api_key: '',
                api_secret: '',
                token: '',
                value: '',
                value_object: '',
                key_value_pairs: [],
                custom_secrets: [],
                tags: []
            });
        }
    }, [initialFormValues, setValue, reset, credentialId]);

    // Re-validate form when credential_type changes to update conditional validation
    useEffect(() => {
        if (watchedCredentialType) {
            trigger([
                'token_url',
                'grant_type',
                'client_id',
                'client_secret',
                'headers',
                'request_body_template',
                'username',
                'password',
            ]);
        }
    }, [watchedCredentialType, trigger]);

    const onWindowClose = useCallback(() => {
        onClose();
        onDialogClose();
        setIsSubmitted(false);
        setErrorMessage('');
        setTestResult(null);
        reset();
    }, [onClose, onDialogClose, reset]);

    useEffect(() => {
        if (!showModal) {
            setTestResult(null);
        }
    }, [showModal]);

    useEffect(() => {
        if (testResult || (isSubmitted && (errorMessage || !isValid))) {
            const el = document.getElementById('credential-modal-body');
            if (el) {
                el.scrollTo({
                    top: el.scrollHeight,
                    behavior: 'smooth'
                });
            }
        }
    }, [testResult, isSubmitted, errorMessage, isValid]);

    const buildConfigObject = useCallback((data: CredentialFormDetails) => {
        const { credential_type } = data;
        
        // Determine which config fields to include based on credential type
        const config: Record<string, any> = {};
        
        const coerceTokenTtl = (v: unknown): number | undefined => {
            if (typeof v === 'number' && !Number.isNaN(v) && v >= 0) return v;
            if (typeof v === 'string' && v.trim() !== '') {
                const n = Number(v);
                if (!Number.isNaN(n) && n >= 0) return n;
            }
            return undefined;
        };
        
        // OAuth2 Client & Password
        if (credential_type === 'oauth2') {
            if (data.token_url) config.token_url = data.token_url;
            if (data.scope) config.scope = data.scope;
            if (data.grant_type) config.grant_type = data.grant_type;
            const oauthTtl = coerceTokenTtl(data.token_ttl_seconds);
            if (oauthTtl !== undefined) config.token_ttl_seconds = oauthTtl;
        }
        
        // API Key
        if (credential_type === 'api_key') {
            if (data.header_name) config.header_name = data.header_name;
            if (data.prefix) config.prefix = data.prefix;
        }

        // Basic Auth
        if (credential_type === 'basic_auth') {
            if (data.token_url) config.token_url = data.token_url.trim();
            if (data.headers?.trim()) config.headers = data.headers.trim();
            if (data.request_body_template?.trim()) config.request_body_template = data.request_body_template.trim();

            config.http_method = data.http_method || 'POST';
            config.response_token_path = data.response_token_path || 'access_token';

            const parsedHeaders = parseJsonObject(data.headers);
            config.content_type = parsedHeaders['Content-Type']
                ? String(parsedHeaders['Content-Type'])
                : 'application/x-www-form-urlencoded';

            const parsedPayload = parseJsonObject(data.request_body_template);
            config.grant_type = parsedPayload.grant_type
                ? String(parsedPayload.grant_type)
                : 'client_credentials';

            const basicTtl = coerceTokenTtl(data.token_ttl_seconds);
            if (basicTtl !== undefined) config.token_ttl_seconds = basicTtl;
        }
        
        // Custom
        if (credential_type === 'custom') {
            if (data.token_url) config.token_url = data.token_url;
            if (data.http_method) config.http_method = data.http_method;
            if (data.request_body_template) config.request_body_template = data.request_body_template;
            if (data.response_token_path) config.response_token_path = data.response_token_path;
            if (data.response_expiry_path) config.response_expiry_path = data.response_expiry_path;
            if (data.headers) config.headers = data.headers;
            const customTtl = coerceTokenTtl(data.token_ttl_seconds);
            if (customTtl !== undefined) config.token_ttl_seconds = customTtl;
        }
        
        // Key Value
        if (credential_type === 'key_value') {
            if (data.key) config.key = data.key;
            if (data.value_type) config.value_type = data.value_type;
        }

        // Cookie Password
        if (credential_type === 'cookie_password') {
            if (data.token_url) config.token_url = data.token_url;
            if (data.access_cookie_name) config.access_cookie_name = data.access_cookie_name;
            const cookieTtl = coerceTokenTtl(data.token_ttl_seconds);
            if (cookieTtl !== undefined) config.token_ttl_seconds = cookieTtl;
        }
        
        return Object.keys(config).length > 0 ? config : undefined;
    }, []);

    const buildSecretsObject = useCallback((data: CredentialFormDetails) => {
        const { credential_type } = data;
        
        const secrets: Record<string, string> = {};
        
        // OAuth2 Client
        if (credential_type === 'oauth2') {
            if (data.client_id) secrets.client_id = data.client_id;
            if (data.client_secret) secrets.client_secret = data.client_secret;
        }
        
        // OAuth2 Password
        if (credential_type === 'oauth2' && data.grant_type === 'password') {
            if (data.client_id) secrets.client_id = data.client_id;
            if (data.client_secret) secrets.client_secret = data.client_secret;
            if (data.username) secrets.username = data.username;
            if (data.password) secrets.password = data.password;
        }
        
        // API Key
        if (credential_type === 'api_key') {
            if (data.api_key) secrets.api_key = data.api_key;
        }
        
        // Basic Auth
        if (credential_type === 'basic_auth') {
            if (data.username) secrets.username = data.username;
            if (data.password) secrets.password = data.password;
        }
        
        // Custom
        if (credential_type === 'custom') {
            if (Array.isArray(data.custom_secrets)) {
                data.custom_secrets.forEach(({ key, value }) => {
                    const trimmedKey = (key || '').trim();
                    if (trimmedKey) {
                        secrets[trimmedKey] = value ?? '';
                    }
                });
            }
        }
        
        // Bearer Static
        if (credential_type === 'bearer_static') {
            if (data.token) secrets.token = data.token;
        }
        
        // Key Value
        if (credential_type === 'key_value') {
            if (data.value_type === 'string') {
                if (data.value) secrets.value = data.value;
            } else if (data.value_type === 'key_value') {
                if (Array.isArray(data.key_value_pairs) && data.key_value_pairs.length > 0) {
                    const kvObject = data.key_value_pairs.reduce<Record<string, string>>((acc, pair) => {
                        const trimmedKey = (pair.key || '').trim();
                        if (trimmedKey) {
                            acc[trimmedKey] = pair.value ?? '';
                        }
                        return acc;
                    }, {});
                    secrets.value = kvObject as unknown as string;
                }
            } else if (data.value_type === 'object') {
                if (data.value_object) {
                    const rawValue = data.value_object.trim();
                    try {
                        secrets.value = JSON.parse(rawValue);
                    } catch (error){
                        secrets.value = rawValue;
                        if (error)
                            throw new Error('Invalid JSON object');
                    }
                }
            }
        }

        // Cookie Password
        if (credential_type === 'cookie_password') {
            if (data.username) secrets.username = data.username;
            if (data.password) secrets.password = data.password;
        }
        
        return Object.keys(secrets).length > 0 ? secrets : undefined;
    }, []);

    const saveCredential = useCallback(async (payload: CredentialPayload) => {
        if (credentialId) {
            const response = await updateCredential({
                credential_id: credentialId,
                payload: payload
            }).unwrap();
            CtyShowToast(checkType.success, response?.message || t('Credential updated successfully'));
        } else {
            const response = await createCredential(payload).unwrap();
            CtyShowToast(checkType.success, response?.message || t('Credential created successfully'));
        }
    }, [credentialId, updateCredential, createCredential, t]);

    const handleFormSubmit = async () => {
        setIsSubmitted(true);
        setErrorMessage('');
        const isValidForm = await trigger();
        if (!isValidForm) {
            return;
        }

        try {
            const data = getValues();
            const {
                token_url, grant_type,scope, token_ttl_seconds,
                header_name, prefix, http_method, request_body_template,
                response_token_path, response_expiry_path, headers, key,
                client_id, client_secret, username, password,
                api_key, api_secret, token, value, value_object, value_type, key_value_pairs,
                custom_secrets, tags, ...restData
            } = data;
            
            const payload = {
                ...restData,
                ...(!credentialId ? optionalCodePayload(credentialCode) : {}),
            } as CredentialPayload;

            const config = buildConfigObject(data);
            if (config) {
                payload.config = config;
            }

            const secrets = buildSecretsObject(data);
            if (secrets) {
                payload.secrets = secrets;
            }

            if (Array.isArray(tags) && tags.length > 0) {
                payload.tags = tags;
            } else if (tags !== undefined) {
                payload.tags = [];
            }

            await saveCredential(payload);
            onWindowClose();
            if (onSuccess) {
                onSuccess();
            }
        } catch (error) {
            // Toast error message would be better here but it's not working as expected
            setErrorMessage((error as Error)?.message || t('Error saving credential'));
        }
    };

    const handleTestCredential = async () => {
        if (!credentialId) return;
        setTestResult(null);
        try {
            const response = await triggerTestCredential(credentialId).unwrap();
            setTestResult({
                type: 'success',
                message: response?.message || t('Credential validated successfully')
            });
        } catch (error) {
            setTestResult({
                type: 'error',
                message: (error as Error)?.message || t('Credential validation failed')
            });
        }
    };

    const header = credentialId ? t('Edit Credential') : t('Create New Credential');
    const showFooter = !isLoadingCredential || !credentialId;

    return (
        <Modal 
            className="max-h-[90%]" 
            backdrop={'blur'} 
            isOpen={showModal} 
            onClose={onWindowClose} 
            isDismissable={false}
            size="md"
            scrollBehavior="inside"
        >
            <ModalContent>
                <ModalHeader className="flex flex-col gap-1">{header}</ModalHeader>
                <ModalBody className="overflow-y-auto" id="credential-modal-body">
                    {isLoadingCredential && credentialId ? (
                        <div className="flex justify-center items-center h-64">
                            <Spinner size="lg" />
                        </div>
                    ) : (
                <CredentialForm
                    control={control}
                    errors={errors}
                    watch={watch}
                    setValue={setValue}
                    code={credentialId ? (credentialDetails?.code ?? '') : credentialCode}
                    isCreateMode={!credentialId}
                    onCodeChange={setCredentialCode}
                />
                    )}
                    {testResult && credentialId  && (
                        <div className={`mb-3 rounded-md border p-3 text-sm ${testResult.type === 'success'
                            ? 'border-success bg-success/10 text-success'
                            : 'border-danger bg-danger/10 text-danger'
                        }`}>
                            {testResult.message}
                        </div>
                    )}
                    {isSubmitted && errorMessage && (
                        <div className="mt-4 p-4 bg-danger/10 border border-danger rounded-md">
                            <h4 className="text-danger font-medium mb-2">{errorMessage}</h4>
                        </div>
                    )}
                    {isSubmitted && !isValid && (
                        <div className="mt-4 p-4 bg-danger/10 border border-danger rounded-md">
                            <h4 className="text-danger font-medium mb-2">{t('Please fix the following errors:')}</h4>
                            <ul className="text-danger text-sm space-y-1">
                                {errors.name && <li>• {errors.name.message}</li>}
                                {errors.description && <li>• {errors.description.message}</li>}
                                {errors.credential_type && <li>• {errors.credential_type.message}</li>}
                                {errors.token_url && <li>• {errors.token_url.message}</li>}
                                {errors.grant_type && <li>• {errors.grant_type.message}</li>}
                                {errors.scope && <li>• {errors.scope.message}</li>}
                                {errors.token_ttl_seconds && <li>• {errors.token_ttl_seconds.message}</li>}
                                {errors.access_cookie_name && <li>• {errors.access_cookie_name.message}</li>}
                                {errors.header_name && <li>• {errors.header_name.message}</li>}
                                {errors.prefix && <li>• {errors.prefix.message}</li>}
                                {errors.http_method && <li>• {errors.http_method.message}</li>}
                                {errors.request_body_template && <li>• {errors.request_body_template.message}</li>}
                                {errors.response_token_path && <li>• {errors.response_token_path.message}</li>}
                                {errors.response_expiry_path && <li>• {errors.response_expiry_path.message}</li>}
                                {errors.headers && <li>• {errors.headers.message}</li>}
                                {errors.key && <li>• {errors.key.message}</li>}
                                {errors.client_id && <li>• {errors.client_id.message}</li>}
                                {errors.client_secret && <li>• {errors.client_secret.message}</li>}
                                {errors.username && <li>• {errors.username.message}</li>}
                                {errors.password && <li>• {errors.password.message}</li>}
                                {errors.api_key && <li>• {errors.api_key.message}</li>}
                                {errors.api_secret && <li>• {errors.api_secret.message}</li>}
                                {errors.token && <li>• {errors.token.message}</li>}
                                {errors.value && <li>• {errors.value.message}</li>}
                                {errors.tags && <li>• {errors.tags.message}</li>}
                            </ul>
                        </div>
                    )}
                </ModalBody>
                {showFooter && (
                    <ModalFooter>
                        <CtyButton 
                            id={'credential-cancel'} 
                            variant={'bordered'} 
                            className='w-full' 
                            color="primary" 
                            onPress={onWindowClose}
                        >
                            {t('Cancel')}
                        </CtyButton>
                        {credentialId && (
                            <CtyButton
                                id={'credential-test'}
                                variant={'bordered'}
                                className='w-full'
                                color="primary"
                                isLoading={isTestingCredential}
                                onPress={handleTestCredential}
                                isDisabled={!hasCredentialsAccess}
                            >
                                {t('Test')}
                            </CtyButton>
                        )}
                        <CtyButton 
                            id={'credential-submit'} 
                            className="w-full" 
                            color="primary" 
                            isLoading={isLoading} 
                            isDisabled={!hasCredentialsAccess || (isSubmitted && !isValid)}
                            onPress={handleFormSubmit}
                        >
                            {t('Submit')}
                        </CtyButton>
                    </ModalFooter>
                )}
            </ModalContent>
        </Modal>
    );
};

export default CredentialModal;
